import React from 'react';
import { useSelector } from 'react-redux';
import { useEnvironment } from '@wix/yoshi-flow-editor';

import type { IFeedItem } from 'api/feed/types';
import { selectGroupSlugById } from 'store/selectors';
import { useRelativeTimeFormatter } from 'common/hooks';

import { Box } from 'wui/Box';
import { Stack } from 'wui/Stack';
import { Show } from 'wui/Show';
import { CardHeader } from 'wui/CardHeader';
import { Typography } from 'wui/Typography';
import { Avatar } from 'wui/Avatar';

import { Link } from '../Link';
import { RoleIcon } from '../RoleIcon';
import { ProfileLink } from '../ProfileLink';
import { MemberBadges } from '../MemberBadges';

import { ActivityDescription } from './ActivityDescription';
import { FeedItemMenu } from './FeedItemMenu';

import { getAriaId } from './a11y';
import classes from './FeedItem.scss';

interface IProps {
  item: IFeedItem;

  promote?: boolean;
}

export function FeedItemHeader(props: IProps) {
  const { item, promote } = props;

  const { isMobile } = useEnvironment();
  const rtf = useRelativeTimeFormatter();

  const feedItemId = item.feedItemId as string;
  const groupId = item.applicationContext?.contextId as string;

  const slug = useSelector(selectGroupSlugById(groupId));

  return (
    <CardHeader
      action={<FeedItemMenu item={item} />}
      avatar={
        <ProfileLink profile={item.createdBy} tabIndex={-1}>
          <Avatar
            lettersLimit={2}
            name={item.createdBy.name}
            src={item.createdBy.imageUrl}
            size={isMobile ? 'medium' : 'large'}
          />
        </ProfileLink>
      }
      title={
        <Box
          as="div"
          verticalAlign="middle"
          id={getAriaId(feedItemId, 'author')}
        >
          <Typography
            variant={isMobile ? 'p2-14' : 'p2-16'}
            as="div"
            className={classes.author}
          >
            <ProfileLink data-hook="author name" profile={item.createdBy} />
            <RoleIcon role={item.createdBy?.role} />
          </Typography>
        </Box>
      }
      subtitle={
        <Typography
          secondary
          variant={isMobile ? 'p2-12' : 'p2-14'}
          as="div"
          className={classes.subtitle}
        >
          <Box gap="SP2" direction="vertical" className={classes.timestamp}>
            <Stack
              as="div"
              flex={false}
              separator={<span> · </span>}
              id={getAriaId(feedItemId, 'meta')}
            >
              <Link
                state="group.discussion.post"
                params={{ slug, feedItemId }}
                className={classes.timestamp}
              >
                {rtf(item.createdAt as Date)}
              </Link>
              <Show if={!!item.activity || promote}>
                <ActivityDescription full={promote} item={item} />
              </Show>
            </Stack>
            <MemberBadges member={item.createdBy} />
          </Box>
        </Typography>
      }
    />
  );
}

FeedItemHeader.displayName = 'FeedItemHeader';
